import type React from "react";
import { useContext } from "react";

import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { CareEvent } from "api/models/CareEvent";
import { useProfileContext } from "contexts/ProfileContext";
import { CommunicationGroupIcon } from "routes/dashboard/default/components/MedicalNotesTable/CommunicationGroupIcon";
import { RecordNoteHeader } from "routes/patients/PatientProfile/components/Information/components/RecordNoteHeader";
import { MUITextInput } from "shared/atoms/inputs";
import { MUIAccordion, MUIAccordionDetails, MUIAccordionSummary } from "shared/atoms/MUIAccordion";
import type { MedicalNoteSectionConfig } from "types";
import { MedicalNoteSectionTypes } from "types";
import { CurrentPatientContext } from "utils/contexts";
import { userIsPartner } from "utils/profile/profileHelper";

import {
  DynamicSection,
  SectionRow,
  SystemSuggestionsSection,
  TopSection,
} from "../../../DynamicMedicalNotes/components";
import { reduceCareEventToFields } from "../../../DynamicMedicalNotes/helpers/careEventHelpers";
import {
  getAllFieldNamesFromConfig,
  shouldShowSection,
} from "../../../DynamicMedicalNotes/helpers/dynamicMedicalNoteHelpers";
import { Form } from "../../../DynamicMedicalNotes/utils/formStyles";
import { MedicalNoteLabel } from "../../../PatientHeader/SignNotesTable/components/MedicalNoteLabel";
import { Addenda } from "../Addenda";
import { PDFDownloadButton } from "../PDFDownload";

interface Props {
  record: CareEvent;
  config: MedicalNoteSectionConfig[];
}

export const RecordNote: React.FC<Props> = ({ record, config }) => {
  const { t } = useTranslation();
  const { patient } = useContext(CurrentPatientContext);
  const { profile } = useProfileContext();
  const isPartner = userIsPartner(profile);
  const readonly = true; // turn into state when implementing Amendments

  const fieldsNames = getAllFieldNamesFromConfig(config, false);
  const form = useForm({
    defaultValues: reduceCareEventToFields(fieldsNames, record, true),
    mode: "onChange",
  });

  const patientBirthday =
    patient.birthday && new Date(patient.birthday).toLocaleDateString(profile?.locale, { timeZone: "UTC" });

  const getSection = (section: MedicalNoteSectionConfig) => {
    const onValueChange = () => null;

    if (section.type === MedicalNoteSectionTypes.TOP) {
      return (
        <TopSection
          readonly={readonly}
          fields={section.fields}
          key="top-section"
          legacyText={record.medical_record_note}
          onChange={onValueChange}
          label={record.label}
          patientId={record.patient_id}
          payer={record.payer}
          ailment={record.ailment}
          painLocation={record.pain_location}
          patientBillingAddress={record.patient_billing_address}
        />
      );
    }

    if (section.type === MedicalNoteSectionTypes.EXCLUSION) return null;

    if (section.type === MedicalNoteSectionTypes.SUGGESTIONS && section.fields?.length)
      return (
        <SystemSuggestionsSection
          fields={section.fields}
          key="system-suggestions-section"
          collapsible={false}
          backgroundColor="default"
          readOnly
        />
      );

    return (
      <DynamicSection
        key={`dynamic-section-${section.name}`}
        name={section.name}
        readonly={readonly}
        fields={section.fields}
        forcedOptional={false}
        onChange={onValueChange}
        collapsible={false}
        patientId={record.patient_id}
        payer={record.payer}
        label={record.label}
        ailment={record.ailment}
        painLocation={record.pain_location ?? ""}
        isNoteSigned
      />
    );
  };

  return (
    <MUIAccordion>
      <MUIAccordionSummary>
        <RecordNoteHeader date={record.start_time}>
          <CommunicationGroupIcon communicationGroup={record.communication_group} />
          <MedicalNoteLabel weekNumber={record.week_number} careEventLabel={record.label} />
        </RecordNoteHeader>
      </MUIAccordionSummary>
      <MUIAccordionDetails>
        {isPartner && <PDFDownloadButton config={config} record={record} patientBirthday={patientBirthday} />}
        <FormProvider {...form}>
          <Form $open={!readonly}>
            <ContentWrapper>
              <Section>
                <StyledSectionRow>
                  <NameField
                    name="health_care_professional_name"
                    label={t("patients.medical_records.responsible")}
                    value={record.health_care_professional_name}
                    disabled={readonly}
                    unregistered
                  />
                  <DateField
                    name="health_care_professional_type"
                    label={t("patients.medical_records.role")}
                    value={record.health_care_professional_type}
                    disabled={readonly}
                    unregistered
                  />
                </StyledSectionRow>
              </Section>
              {config
                .filter(section => section.fields && section.fields.length > 0)
                .filter(section => shouldShowSection(section, form.getValues))
                .map(section => getSection(section))}
            </ContentWrapper>
          </Form>
        </FormProvider>
        {!isPartner && <Addenda careEventId={record.id} medical_note_addenda={record.medical_note_addenda} />}
      </MUIAccordionDetails>
    </MUIAccordion>
  );
};

const Section = styled.div`
  padding: 20px 20px 0;
  padding-right: 120px;

  ${props => props.theme.belowBreakpoint} {
    padding-right: ${props => props.theme.spacing.S_20};
  }
`;

const StyledSectionRow = styled(SectionRow)`
  ${props => props.theme.belowBreakpoint} {
    flex-direction: column;
  }
`;

const NameField = styled(MUITextInput)`
  flex: 1;
  margin-right: ${props => props.theme.spacing.S_15};

  ${props => props.theme.belowBreakpoint} {
    margin-right: 0;
  }
`;

const DateField = styled(MUITextInput)`
  width: 135px;
  flex: none;

  ${props => props.theme.belowBreakpoint} {
    width: auto;
    flex: 1;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
`;
